import React, { FC, useEffect, useState } from 'react'
import { Helmet } from 'react-helmet'
import { useDispatch, useSelector } from 'react-redux'
import { RootState } from '../../../../states/containers/store'
import Loading from '../../../components/common/Loading'
import { useTranslation } from 'react-i18next'
import { setErrors } from '../../../../states/containers/common/errorsSlice'
import { ReactSVG } from 'react-svg'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { uniqueId } from 'lodash'
import { fetchIpRestrictionList, updateIpRestrictionList } from '../../../../states/containers/setting/systemSlice'
import { validateIpAddress } from '../../../../utils/validate'
import { useAlert } from 'react-alert'
import { getIpAddress } from '../../../../states/apis/setting'
import { Link, useParams } from 'react-router-dom'
import { setPath } from '../../../../utils/url'
import { LangValue } from '../../../../states/types/common'
import { URL } from '../../../constants/url'

interface IpList {
  ip_address: string,
  comment: string,
  error: boolean,
  unique_id: string,
}

const Index: FC = () => {
  const { t } = useTranslation()
  const dispatch = useDispatch()
  const alert = useAlert()
  const { language } = useParams<{ language: LangValue }>()

  const {
    system,
    login,
  } = useSelector((state: RootState) => (state))

  const initIpList = {
    ip_address: "",
    comment: "",
    error: false,
    unique_id: 0,
  }

  const [ipAddress, setIpAddress] = useState<string>()
  const [ipList, setIpList] = useState<IpList[]>([])
  const [isSubmit, setIsSubmit] = useState<boolean>(true)

  // ip list
  const addIpList = () => {
    setIpList([...ipList, {
      ...initIpList,
      unique_id: uniqueId(),
    }])
  }

  const deleteIpList = (id: number) => {
    const tmpIpList = ipList.filter((_value, index) => index !== id)
    setIpList(tmpIpList)
  }

  const updateIpAddress = (event: React.ChangeEvent<HTMLInputElement>, index: number) => {
    const tmpIpList = [...ipList]
    if (!validateIpAddress(event.target.value) && event.target.value) {
      tmpIpList[index] = { ...tmpIpList[index], ip_address: event.target.value, error: true }
    } else {
      tmpIpList[index] = { ...tmpIpList[index], ip_address: event.target.value, error: false }
    }
    setIpList(tmpIpList)
  }

  const updateComment = (event: React.ChangeEvent<HTMLInputElement>, index: number) => {
    const tmpIpList = [...ipList]
    tmpIpList[index] = { ...tmpIpList[index], comment: event.target.value }
    setIpList(tmpIpList)
  }

  const handleSubmit = () => {
    dispatch(
      updateIpRestrictionList(
        { list: ipList },
        () => {
          dispatch(fetchIpRestrictionList())
          alert.success(t('setting.common.ip_restriction.alert.success'))
        },
        () => {
          alert.error(t('setting.common.ip_restriction.alert.error'))
        },
      ),
    )
  }

  const ipListHTML = (
    <>
      <section className="setting_page">
        {
          ipList.length ? (
            <table className="flex-table ip_address_list_table">
              <thead>
                <tr>
                  <th>{t('setting.common.ip_restriction.table.ip_address')}</th>
                  <th>{t('setting.common.ip_restriction.table.memo')}</th>
                  <th>{t('setting.common.ip_restriction.table.delete')}</th>
                </tr>
              </thead>
              <tbody>
                <tr>
                  <td>
                    <input
                      type="text"
                      value={t('setting.common.ip_restriction.table.example.ip_address') as string}
                      readOnly
                      disabled
                      className="example"
                    />
                  </td>
                  <td>
                    <input
                      type="text"
                      value={t('setting.common.ip_restriction.table.example.memo') as string}
                      readOnly
                      disabled
                      className="example"
                    />
                  </td>
                  <td />
                </tr>
                {
                  ipList?.map((ip, index) => (
                    <tr key={`ip_${ip.unique_id}`}>
                      <td>
                        <input
                          type="text"
                          defaultValue={ip.ip_address}
                          onChange={(event) => updateIpAddress(event, index)}
                          className={ip.error ? "error" : ""}
                        />
                        {ip.error}
                      </td>
                      <td>
                        <input
                          type="text"
                          defaultValue={ip.comment}
                          onChange={(event) => updateComment(event, index)}
                        />
                      </td>
                      <td>
                        <ReactSVG
                          src="/static/images/icon/icon-trash.svg"
                          className="icon"
                          onClick={() => deleteIpList(index)}
                          title={t('setting.common.ip_restriction.delete_button')}
                        />
                      </td>
                    </tr>
                  ))
                }
              </tbody>
            </table>
          ) : (
            <p style={{ marginLeft: "16px" }}>
              {t('setting.common.ip_restriction.no_data')}
            </p>
          )
        }
      </section>
    </>
  )

  useEffect(() => {
    if (!login.accessToken) return
    getIpAddress()
      .then((result) => {
        setIpAddress(result.ip_address)
      })
      .catch((error) => {
        dispatch(setErrors(error))
      })
  }, [login.accessToken])

  useEffect(() => {
    if (!login.accessToken) return
    dispatch(fetchIpRestrictionList())
  }, [login.accessToken])

  useEffect(() => {
    if (!system.ip_restriction_list) return
    setIpList(system.ip_restriction_list.map((value) => ({ ...value, error: false, unique_id: uniqueId() })))
  }, [system.ip_restriction_list])

  useEffect(() => {
    setIsSubmit(!ipList.find((value) => value.error === true))
  }, [ipList])

  return (
    <section className="setting_page">
      <Helmet>
        <title>{`Qasee - 設定 - 共通${login.role_name === "管理者" ? "管理者" : "共通"}設定設定 - ${t('setting.common.ip_restriction.title')}`}</title>
      </Helmet>
      <section className="breadcrumb">
        設定
        &nbsp;&gt;&nbsp;
        <Link
          to={setPath(URL.SETTING_COMMON.path, language)}
        >
          {`${login.role_name === "管理者" ? "管理者" : "共通"}設定`}
        </Link>
        &nbsp;&gt;&nbsp;
        {t('setting.common.ip_restriction.title')}
        &nbsp;
      </section>
      <section className="page_header">
        <h1>
          {t('setting.common.ip_restriction.title')}
        </h1>
      </section>
      <section className="page_description">
        <p>
          アクセスできるIPアドレスを制限して、セキュリティを強化できます。
        </p>
        <p>
          詳しい設定方法は
          <a href="https://qasee.zendesk.com/hc/ja/articles/39240355393945/" target="_blank" rel="noreferrer">こちら</a>
          をご覧ください。
        </p>
      </section>
      <section className="page_contents content-area">
        <div className="content-block">
          <div className="block-detail">
            <div className="ip_restriction_table_wrapper">
              {
                system.isLoading ? (
                  <Loading />
                ) : (
                  <>
                    {ipListHTML}
                    <div role="none" onClick={addIpList} className="add">
                      <FontAwesomeIcon icon={['fas', 'plus']} style={{ marginRight: '4px' }} fixedWidth />
                      {t('setting.common.ip_restriction.add_button')}
                    </div>
                  </>
                )
              }
            </div>
            <div>
              <div className="ip_restriction_info">
                設定する場合は、現在接続中のIPアドレスを設定してください。設定前に固定IPかどうか確認をお願いいたします。設定後は、設定したIPアドレスのみが接続可能です。
                <br />
                アクセスごとにIPアドレスが変わる動的IPアドレスは、設定しないようご注意ください。
              </div>
              <div style={{ margin: "16px 0" }}>
                {t('setting.common.ip_restriction.ip_address')}
                <span style={{ fontWeight: "bold" }}>
                  {ipAddress}
                </span>
              </div>
              <button type="button" className="button" onClick={handleSubmit} disabled={!isSubmit}>
                編集する
              </button>
            </div>
          </div>
        </div>
      </section>
    </section>
  )
}

export default Index
